import { useState } from "react";

import * as api from "../../lib/api.js";
import { captureAnalytics } from "../analytics";
import { t } from "../contract/strings";
import { Button, Overlay, cx } from "../ui";
import { useOutOfView } from "./top-up";
import {
  defaultPlan,
  packPrice,
  paymentProblem,
  planAction,
  planAskAi,
  planBestValue,
  planCreditsLine,
  planName,
  planPrice,
  planStore,
  type PlanKey,
  type Wallet,
} from "./wallet";

/** Where the Terms say what fair use is: Pro's unlimited Ask AI carries the asterisk. */
export const FAIR_USE_HREF = "/terms/#fair-use";

/**
 * Why the plans can't be changed here, or null when they can: a plan billed in a store is
 * changed in that store, and a plan whose payment failed is fixed first (#1010).
 */
export function plansLocked(wallet: Wallet): string | null {
  const store = planStore(wallet);
  if (store === "apple_iap") return t("plans.locked_app_store");
  if (store === "google_play") return t("plans.locked_google_play");
  if (paymentProblem(wallet.subscription)) return t("plans.locked_payment");
  return null;
}

/**
 * The plans (#1010): Basic, Plus and Pro, and the one button that buys or changes one. Its
 * own overlay, in the Add credits overlay's chrome and labelled Plans; opened by Account's
 * See plans and Change plan, by `/plans` (Home's Choose a plan), by `/account?top_up=1`
 * without a plan, by a pack checkout refused for want of one, and by Ask AI's See plans.
 * No packs here: they are Add credits, for plan holders.
 *
 * Top to bottom: what a credit buys and that summaries and notes are free; the three cards;
 * what every plan shares (Ask AI limits per person, monthly renewal, the reset) and the fair
 * use link; then the button, pinned like Add credits' when the cards run past the screen.
 * A plan opens chosen (`defaultPlan`, #1022): Plus without a plan, the next plan up on one,
 * nothing on Pro; the button follows the choice -- Choose {plan} for a first plan, Upgrade
 * to {plan} (confirmed first, naming today's charge) for a higher one, Switch to {plan} for
 * a lower -- and reads Choose a plan, disabled, while nothing is chosen.
 * A plan billed in a store, or one whose payment failed, locks the cards and takes the
 * button away; one line under the header, above the cards, says why.
 */
export function PlansOverlay({
  wallet,
  onClose,
  onPlanChanged = () => undefined,
}: {
  wallet: Wallet;
  onClose: () => void;
  /** A plan change Dodo accepted (#875), for Account to say and read the wallet again. */
  onPlanChanged?: (plan: PlanKey, effective: "now" | "renewal") => void;
}) {
  const [chosen, setChosen] = useState<PlanKey | null>(() => defaultPlan(wallet));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  /** An upgrade waiting for its confirm, and the confirm's sentence. */
  const [confirming, setConfirming] = useState<string | null>(null);
  const [restingPlace, stuck] = useOutOfView();
  const available = wallet.checkout_available;
  const locked = plansLocked(wallet);
  const move = chosen ? planAction(wallet, chosen) : null;

  function choose(plan: PlanKey) {
    captureAnalytics("plan_selected", { plan });
    setChosen(plan);
    setConfirming(null);
    setError("");
  }

  function failed(err: unknown, fallback: string) {
    console.warn("plan action failed", err);
    const status = (err as { status?: unknown } | null)?.status;
    setError(status === 503 ? t("plans.unavailable") : fallback);
    setBusy(false);
  }

  /** Leave for Dodo's checkout for a first plan; it comes back to /account. */
  async function pay(plan: PlanKey) {
    setBusy(true);
    setError("");
    try {
      const { checkout_url } = await api.planCheckout(plan);
      // Left busy: the page is about to go.
      window.location.assign(checkout_url);
    } catch (err) {
      failed(err, t("billing.error_checkout"));
    }
  }

  /**
   * An upgrade is charged at once, so it is confirmed first, naming today's charge when Dodo
   * can say (a month of the new plan less what is left of this one), else in words.
   */
  async function askToUpgrade(plan: PlanKey) {
    setBusy(true);
    setError("");
    const name = planName(plan);
    let line = t("plans.upgrade_confirm", { plan: name });
    try {
      const preview = await api.previewPlanChange(plan);
      if (preview.amount_minor != null && preview.currency) {
        line = t("plans.upgrade_confirm_amount", {
          plan: name,
          price: packPrice(preview.amount_minor, preview.currency),
        });
      }
    } catch (err) {
      console.warn("plan preview failed", err);
    }
    setBusy(false);
    setConfirming(line);
  }

  /** Upgrade (confirmed) or switch the web plan; Account says what happens next. */
  async function change(plan: PlanKey) {
    setBusy(true);
    setError("");
    try {
      const { effective } = await api.changePlan(plan);
      setBusy(false);
      onPlanChanged(plan, effective);
    } catch (err) {
      setConfirming(null);
      failed(err, t("plans.change_failed"));
    }
  }

  function act() {
    if (!chosen) return;
    captureAnalytics("checkout_started", { kind: "plan" });
    if (move === "upgrade") {
      if (confirming) void change(chosen);
      else void askToUpgrade(chosen);
    } else if (move === "switch") {
      void change(chosen);
    } else {
      void pay(chosen);
    }
  }

  const actionLabel = !chosen
    ? t("plans.choose_none")
    : move === "upgrade"
      ? t("plans.upgrade", { plan: planName(chosen) })
      : move === "switch"
        ? t("plans.switch", { plan: planName(chosen) })
        : busy
          ? t("billing.continuing")
          : t("plans.choose", { plan: planName(chosen) });

  return (
    // The header label in caps, as Add credits' is; the overlay's only title, so it names
    // the dialog too.
    <Overlay
      label={<span className="uppercase">{t("plans.label")}</span>}
      aria-label={t("plans.label")}
      onClose={onClose}
      fill
    >
      <div className="flex-1 flex flex-col w-full pb-13">
        <div data-plans-intro className="my-4 text-left w-full">
          <p className="m-0">{t("billing.top_up_hint")}</p>
          <p className="m-0 mt-1 text-note text-ink-soft">{t("plans.notes_free")}</p>
          {/* Locked plans say why first, before the dimmed cards are read. */}
          {locked ? (
            <p data-plans-locked role="status" className="m-0 mt-3 text-note font-medium text-ink text-balance">
              {locked}
            </p>
          ) : null}
        </div>

        <PlanCards wallet={wallet} chosen={chosen} onChoose={choose} disabled={busy || !available} />

        <div data-plans-notes className="flex flex-col gap-list mt-5 text-note text-ink-soft">
          <p className="m-0">{t("plans.ask_ai_per_person")}</p>
          <p className="m-0">{t("plans.renewal_note")}</p>
          <p className="m-0">{t("plans.credits_reset")}</p>
          <p className="m-0">
            <a href={FAIR_USE_HREF} className="text-ink-soft underline underline-offset-3"
              onClick={() => captureAnalytics("link_opened", { link: "fair_use" })}
            >
              {t("plans.fair_use_short")}
            </a>
          </p>
        </div>

        {/* The button, and any error right under it, pinned to the bottom of the overlay
            when the cards are taller than the screen, as Add credits' is. Locked plans have
            no button: the line at the top says why. */}
        <div
          className={cx(
            "sticky bottom-0 self-stretch -mx-pad px-pad pt-5 pb-3",
            stuck && "bg-(--bg) border-t border-line"
          )}
        >
          {locked ? null : (
            <>
              {/* An upgrade's confirm, over the button that confirms it (#875): what today's
                  charge is, then the same Upgrade to {plan}, and Cancel to step back. */}
              {confirming ? (
                <p data-plan-confirm role="alert" className="m-0 mb-gutter text-note text-ink text-balance">
                  {confirming}
                </p>
              ) : null}
              <Button disabled={!chosen || busy || !available} onClick={act}>
                {actionLabel}
              </Button>
              {confirming ? (
                <Button
                  variant="ghost"
                  className="w-full mt-gutter"
                  disabled={busy}
                  onClick={() => setConfirming(null)}
                >
                  {t("common.cancel")}
                </Button>
              ) : null}
              {!available || error ? (
                <p role="alert" className="m-0 mt-gutter text-note text-danger min-h-status-line">
                  {error || t("plans.unavailable")}
                </p>
              ) : null}
            </>
          )}
        </div>
        <div ref={restingPlace} aria-hidden="true" className="self-stretch h-px -mt-px" />
        <div className="grow-2 basis-0" />
      </div>
    </Overlay>
  );
}

/**
 * The three plan cards (#875, #1010): selectable cards in the packs' box. A card can be
 * chosen when the plan can be bought or changed to from here: any plan with none yet, or a
 * plan above or below an active web plan. The host's own plan carries the "Your plan" pill
 * and cannot be chosen.
 *
 * Each card: the plan's name on the left and its price a month on the right (the price set
 * like a pack's, its "a month" quieter), then its credits a month with about how many hours
 * of meetings they are, and its Ask AI line, with Pro's Best value pill at its right, the
 * card's bottom right corner (#1022). The chosen card
 * takes the accent outline, grown inward, exactly as a chosen pack does.
 */
export function PlanCards({
  wallet,
  chosen,
  onChoose,
  disabled,
}: {
  wallet: Wallet;
  chosen: PlanKey | null;
  onChoose: (plan: PlanKey) => void;
  disabled: boolean;
}) {
  const plans = wallet.plans ?? [];
  const [priceBefore, priceAfter = ""] = t("plans.price_month").split("{price}");
  return (
    <div role="radiogroup" aria-label={t("plans.label")} className="grid gap-2 w-full text-left">
      {plans.map((plan) => {
        const action = planAction(wallet, plan.plan);
        const current = action === "current";
        const selectable = action !== null && !current;
        const selected = selectable && chosen === plan.plan;
        const price = planPrice(plan);
        return (
          <button
            key={plan.plan}
            type="button"
            role="radio"
            data-plan={plan.plan}
            aria-checked={selected}
            aria-current={current ? "true" : undefined}
            disabled={disabled || !selectable}
            onClick={() => onChoose(plan.plan)}
            className={cx(
              "min-w-0 flex flex-col gap-stack w-full bg-surface border rounded-control py-plate px-gutter text-left",
              "button-transition enabled:active:button-press",
              // The host's own plan is shown at full strength: it is not chosen, it is held.
              !current && "disabled:opacity-45",
              selected
                ? "border-accent ring-2 ring-accent ring-inset"
                : "border-line enabled:hover:border-line-strong"
            )}
          >
            <span className="flex items-baseline justify-between gap-stack">
              <span className="flex items-center gap-tight min-w-0">
                <span className="font-title text-hero font-bold tracking-headline text-ink">
                  {planName(plan.plan)}
                </span>
                {current ? <Pill>{t("plans.current")}</Pill> : null}
              </span>
              {price === null ? null : (
                <span className="min-w-0 text-right text-note text-ink-soft">
                  {priceBefore}
                  <span className="whitespace-nowrap font-title text-[calc(var(--text-note)*1.6)] font-bold tracking-headline text-ink">
                    {price}
                  </span>
                  {priceAfter}
                </span>
              )}
            </span>
            <span className="flex flex-col gap-list text-note text-ink-soft">
              <span className="font-medium text-ink">{planCreditsLine(plan.web_credits)}</span>
              {/* Pro's Best value at the card's bottom right, centred on the Ask AI line. */}
              <span className="flex items-center justify-between gap-stack">
                <span className="min-w-0">{planAskAi(plan)}</span>
                {planBestValue(wallet, plan.plan) ? <Pill>{t("billing.pack_best_value")}</Pill> : null}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

/** The best-value badge's pill: "Your plan" on the host's own plan, "Best value" on Pro. */
function Pill({ children }: { children: string }) {
  return (
    <span className="block flex-none whitespace-nowrap rounded-pill bg-accent-soft py-tight pl-chip-x pr-[calc(var(--spacing-chip-x)-var(--tracking-label))] font-code text-count font-semibold uppercase tracking-label text-accent [text-box:trim-both_cap_alphabetic]">
      {children}
    </span>
  );
}
